import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { computeManualExpenseAmounts, formatInstallationReference, installationMatchesSearchQuery, MANUAL_EXPENSE_SCOPE_LABELS, parseEuroInputToCents } from "@shared/finance/manualExpenses";
import { AlertCircle, Loader2, Plus, ChevronsUpDown } from "lucide-react";
import { SupplierCreateModal } from "./SupplierCreateModal";
import { Command, CommandInput, CommandEmpty, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const optionalIdSchema = z.string().default("none");
const NO_CATEGORY_VALUE = "__manual_expense_no_category__";

const formSchema = z.object({
  concept: z.string().trim().min(1, "El concepto es obligatorio"),
  description: z.string().max(2000).optional(),
  categoryId: optionalIdSchema,
  categoryCode: z.string().trim().max(50).optional(),
  installationId: optionalIdSchema,
  supplierId: optionalIdSchema,
  scope: z.enum(["installation", "general_overhead", "warehouse", "unallocated"]),
  expenseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Fecha inválida"),
  expectedPaymentDate: z.string().optional(),
  externalReference: z.string().max(200).optional(),
  taxableBase: z.string().refine((v) => {
    try {
      parseEuroInputToCents(v);
      return true;
    } catch {
      return false;
    }
  }, "Base imponible inválida"),
  vatRate: z.enum(["0", "4", "10", "21"]),
  notes: z.string().max(2000).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  editId?: number | null;
  onSuccess: () => void;
  onCancel: () => void;
}

function toOptionalNumber(value: string): number | undefined {
  return value && value !== "none" ? Number(value) : undefined;
}

function toNullableNumber(value: string): number | null {
  return value && value !== "none" ? Number(value) : null;
}

function formatMoney(value: string | number | null | undefined): string {
  if (value == null) return "";
  return Number(value).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function installationMatchesSearch(installation: any, search: string): boolean {
  return installationMatchesSearchQuery({
    id: installation.id,
    installationCode: installation.installationCode,
    expedienteCode: installation.installationCode,
    clientId: installation.clientId,
    clientName: installation.clientName,
    siteName: installation.siteName,
    workDescription: installation.workDescription,
    name: installation.name,
    address: installation.address,
  }, search);
}

function installationToReference(installation: any) {
  return {
    id: installation.id,
    installationCode: installation.installationCode ?? null,
    expedienteCode: installation.installationCode ?? null,
    clientId: installation.clientId ?? null,
    clientName: installation.clientName ?? null,
    siteName: installation.siteName ?? null,
    workDescription: installation.workDescription ?? installation.name ?? null,
    name: installation.workDescription ?? installation.name ?? null,
    address: installation.address ?? null,
    status: installation.status ?? null,
  };
}

export function ManualExpenseForm({ editId, onSuccess, onCancel }: Props) {
  const utils = trpc.useUtils();
  const [supplierComboOpen, setSupplierComboOpen] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const isEdit = editId != null;
  const initializedEditId = useRef<number | null>(null);
  const [installationSearch, setInstallationSearch] = useState("");
  const { data: existing } = trpc.manualExpenses.getById.useQuery({ id: editId! }, { enabled: isEdit });
  const categoriesQuery = trpc.financialCategories.list.useQuery({ includeInactive: false });
  const installationsQuery = trpc.installations.list.useQuery();
  const suppliersQuery = trpc.productCatalog.listSuppliers.useQuery({ active: true });

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      concept: "",
      description: "",
      categoryId: "none",
      categoryCode: NO_CATEGORY_VALUE,
      installationId: "none",
      supplierId: "none",
      scope: "unallocated",
      expenseDate: "",
      expectedPaymentDate: "",
      externalReference: "",
      taxableBase: "",
      vatRate: "21",
      notes: "",
    },
  });

  useEffect(() => {
    if (!existing || !isEdit || existing.id !== editId || initializedEditId.current === editId) return;
    initializedEditId.current = editId!;
    reset({
      concept: existing.concept,
      description: existing.description ?? "",
      categoryId: existing.categoryId ? String(existing.categoryId) : "none",
      categoryCode: existing.categoryCode || NO_CATEGORY_VALUE,
      installationId: existing.installationId ? String(existing.installationId) : "none",
      supplierId: existing.supplierId ? String(existing.supplierId) : "none",
      scope: existing.scope as any,
      expenseDate: existing.expenseDate,
      expectedPaymentDate: existing.expectedPaymentDate ?? "",
      externalReference: existing.externalReference ?? "",
      taxableBase: String(existing.taxableBase ?? ""),
      vatRate: String(Math.trunc(Number(existing.vatRate))) as any,
      notes: existing.notes ?? "",
    });
  }, [existing, reset, editId, isEdit]);

  const scope = watch("scope");
  const selectedInstallationId = watch("installationId");
  const taxableBaseInput = watch("taxableBase") || "0";
  const vatRateInput = watch("vatRate") || "21";
  let computed = { vatAmount: "0.00", totalAmount: "0.00" };
  try {
    computed = computeManualExpenseAmounts(parseEuroInputToCents(taxableBaseInput), Number(vatRateInput) as any);
  } catch {
    computed = { vatAmount: "0.00", totalAmount: "0.00" };
  }

  const createMutation = trpc.manualExpenses.create.useMutation({ onSuccess });
  const updateMutation = trpc.manualExpenses.update.useMutation({ onSuccess });

  const onSubmit = (values: FormValues) => {
    const taxableBaseCents = parseEuroInputToCents(values.taxableBase);
    const categoryCode = values.categoryCode === NO_CATEGORY_VALUE ? undefined : values.categoryCode || undefined;
    if (isEdit) {
      updateMutation.mutate({
        id: editId!,
        concept: values.concept,
        description: values.description || undefined,
        categoryId: toNullableNumber(values.categoryId),
        categoryCode,
        installationId: values.scope === "installation" ? toNullableNumber(values.installationId) : null,
        supplierId: toNullableNumber(values.supplierId),
        scope: values.scope,
        expenseDate: values.expenseDate,
        expectedPaymentDate: values.expectedPaymentDate || undefined,
        taxableBaseCents,
        vatRate: Number(values.vatRate) as any,
        externalReference: values.externalReference ?? "",
        notes: values.notes || undefined,
      });
    } else {
      createMutation.mutate({
        concept: values.concept,
        description: values.description || undefined,
        categoryId: toOptionalNumber(values.categoryId),
        categoryCode,
        installationId: values.scope === "installation" ? toOptionalNumber(values.installationId) : undefined,
        supplierId: toOptionalNumber(values.supplierId),
        scope: values.scope,
        expenseDate: values.expenseDate,
        expectedPaymentDate: values.expectedPaymentDate || undefined,
        taxableBaseCents,
        vatRate: Number(values.vatRate) as any,
        externalReference: values.externalReference ?? "",
        notes: values.notes || undefined,
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const categories = categoriesQuery.data ?? [];
  const installations = installationsQuery.data ?? [];
  const filteredInstallations = useMemo(
    () => installations.filter((installation: any) => installationMatchesSearch(installation, installationSearch)).slice(0, 80),
    [installations, installationSearch],
  );
  const suppliers = suppliersQuery.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{isEdit ? "Editar gasto manual" : "Nuevo gasto manual"}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2 space-y-1">
            <Label htmlFor="concept">Concepto *</Label>
            <Input id="concept" {...register("concept")} />
            {errors.concept && <p className="text-xs text-destructive">{errors.concept.message}</p>}
          </div>

          <div className="sm:col-span-2 space-y-1">
            <Label htmlFor="description">Descripción</Label>
            <Textarea id="description" {...register("description")} rows={2} />
          </div>

          <div className="space-y-1">
            <Label>Categoría *</Label>
            <Select value={watch("categoryCode")} onValueChange={(v) => setValue("categoryCode", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CATEGORY_VALUE}>Sin categoría</SelectItem>
                {categories.map((category: any) => (
                  <SelectItem key={category.code} value={category.code}>{category.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Proveedor</Label>
            <Popover open={supplierComboOpen} onOpenChange={setSupplierComboOpen}>
              <PopoverTrigger asChild><Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal">
                <span className="truncate">{suppliers.find((supplier: any) => String(supplier.id) === watch("supplierId"))?.name ?? "Sin proveedor"}</span>
                <ChevronsUpDown className="h-4 w-4 shrink-0" />
              </Button></PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command><CommandInput placeholder="Buscar por nombre o NIF…" /><CommandList>
                  <CommandEmpty>Sin resultados.</CommandEmpty><CommandGroup>
                    <CommandItem value="Sin proveedor" onSelect={() => { setValue("supplierId", "none"); setSupplierComboOpen(false); }}>Sin proveedor</CommandItem>
                    {suppliers.map((supplier: any) => <CommandItem key={supplier.id} value={`${supplier.name} ${supplier.taxId ?? ""}`} onSelect={() => {
                      setValue("supplierId", String(supplier.id), { shouldDirty: true }); setSupplierComboOpen(false);
                    }}>{supplier.name}{supplier.taxId ? ` · ${supplier.taxId}` : ""}</CommandItem>)}
                    <CommandItem value="Crear nuevo proveedor" onSelect={() => { setSupplierComboOpen(false); setSupplierModalOpen(true); }}><Plus className="mr-2 h-4 w-4" />Crear proveedor</CommandItem>
                  </CommandGroup></CommandList></Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <Label>¿Dónde se imputa este gasto? *</Label>
            <Select value={scope} onValueChange={(v) => {
              setValue("scope", v as any);
              if (v !== "installation") setValue("installationId", "none");
            }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(MANUAL_EXPENSE_SCOPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>N.º expediente</Label>
            {scope === "installation" && (
              <Input
                value={installationSearch}
                onChange={(event) => setInstallationSearch(event.target.value)}
                placeholder="Buscar por N.º expediente, cliente, trabajo o dirección"
              />
            )}
            <Select
              value={selectedInstallationId}
              onValueChange={(v) => setValue("installationId", v)}
              disabled={scope !== "installation"}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin expediente</SelectItem>
                {filteredInstallations.map((installation: any) => (
                  <SelectItem key={installation.id} value={String(installation.id)}>
                    {formatInstallationReference(installationToReference(installation))}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scope === "installation" && selectedInstallationId === "none" && (
              <p className="text-xs text-destructive">El N.º expediente es obligatorio para imputar a una instalación.</p>
            )}
          </div>

          {scope === "unallocated" && (
            <div className="sm:col-span-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Este gasto queda pendiente de imputar y solo puede permanecer en borrador o cancelado.</span>
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="expenseDate">Fecha del gasto *</Label>
            <Input id="expenseDate" type="date" {...register("expenseDate")} />
            {errors.expenseDate && <p className="text-xs text-destructive">{errors.expenseDate.message}</p>}
          </div>

          <div className="space-y-1">
            <Label htmlFor="expectedPaymentDate">Fecha prevista de pago</Label>
            <Input id="expectedPaymentDate" type="date" {...register("expectedPaymentDate")} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="externalReference">Referencia externa</Label>
            <Input id="externalReference" {...register("externalReference")} />
          </div>

          <div className="space-y-1">
            <Label htmlFor="taxableBase">Base imponible (€) *</Label>
            <Input id="taxableBase" inputMode="decimal" {...register("taxableBase")} placeholder="0,00" />
            {errors.taxableBase && <p className="text-xs text-destructive">{errors.taxableBase.message}</p>}
          </div>

          <div className="space-y-1">
            <Label>Tipo IVA *</Label>
            <Select value={watch("vatRate")} onValueChange={(v) => setValue("vatRate", v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="4">4%</SelectItem>
                <SelectItem value="10">10%</SelectItem>
                <SelectItem value="21">21%</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 grid grid-cols-2 gap-4 rounded-md bg-muted p-3 text-sm">
            <div><span className="text-muted-foreground">Cuota IVA: </span><span className="font-medium">{formatMoney(computed.vatAmount)} €</span></div>
            <div><span className="text-muted-foreground">Total: </span><span className="font-semibold">{formatMoney(computed.totalAmount)} €</span></div>
          </div>

          <div className="sm:col-span-2 space-y-1">
            <Label htmlFor="notes">Notas</Label>
            <Textarea id="notes" {...register("notes")} value={watch("notes") ?? ""} rows={2} />
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2">
          <Button type="submit" disabled={isPending || (scope === "installation" && selectedInstallationId === "none")}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? "Guardar cambios" : "Crear gasto"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
          {(createMutation.error || updateMutation.error) && (
            <p className="text-xs text-destructive">{createMutation.error?.message || updateMutation.error?.message}</p>
          )}
        </CardFooter>
      </form>
      <SupplierCreateModal open={supplierModalOpen} onOpenChange={setSupplierModalOpen} onCreated={(supplier) => {
        utils.productCatalog.listSuppliers.invalidate();
        setValue("supplierId", String(supplier.id), { shouldDirty: true });
        setSupplierModalOpen(false);
      }} />
    </Card>
  );
}
